/**
 * Pagination Composable for Pinia Stores
 *
 * Provides reusable pagination, sorting, and filtering logic that can be used
 * by any store to eliminate code duplication across paginated interfaces.
 */

import { ref, type Ref } from 'vue';
import { api } from 'src/boot/axios';
import { getResponseData } from 'utils';

export interface PaginationState {
  sortBy: string;
  page: number;
  perPage: number;
  lastPage: number;
  total: number;
  totalPages: number;
}

export interface PaginationData {
  current_page: number;
  per_page: number;
  total_pages: number;
  total: number;
}

export interface FetcherConfig {
  endpoint: string;
  buildQueryString: () => string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
  rethrowStatuses?: number[];
}

/**
 * Pagination composable that provides reactive state and methods
 * for handling paginated data in stores
 *
 * @param defaultSortBy - Default sorting field
 * @param defaultPerPage - Default items per page
 * @returns Object with reactive state and pagination methods
 */
export function usePagination<T>(
  defaultSortBy = '-updated_at',
  defaultPerPage = 12
) {
  // Reactive state
  const items: Ref<T[]> = ref([]);
  const loading = ref(false);
  const pagination = ref<PaginationState>({
    sortBy: defaultSortBy,
    page: 1,
    perPage: defaultPerPage,
    lastPage: 1,
    total: 0,
    totalPages: 0,
  });

  // Virtual scrolling state
  const hasMoreData = ref(true);
  const loadingMore = ref(false);

  /**
   * Builds query parameters for API requests
   *
   * @param additionalFilters - Store-specific filters to add
   * @returns URL-encoded query string
   */
  const buildQueryParams = (
    additionalFilters: Record<string, any> = {}
  ): string => {
    const params = new URLSearchParams();

    // Core pagination parameters
    params.set('sort', pagination.value.sortBy);
    params.set('pg', pagination.value.page.toString());
    params.set('ps', pagination.value.perPage.toString());

    // Add any additional filters passed by specific stores
    Object.entries(additionalFilters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        const filterValue = Array.isArray(value)
          ? value.join(',')
          : value.toString();
        params.set(`filter[${key}]`, filterValue);
      }
    });

    return params.toString();
  };

  /**
   * Updates pagination state from API response
   *
   * @param paginationData - Pagination data from API response
   */
  const updatePagination = (paginationData: PaginationData): void => {
    pagination.value = {
      ...pagination.value,
      page: paginationData.current_page,
      perPage: paginationData.per_page,
      lastPage: paginationData.total_pages,
      total: paginationData.total,
      totalPages: paginationData.total_pages,
    };
  };

  /**
   * Navigate to next page if available
   *
   * @param fetchDataFn - Function to call for fetching data
   * @returns Promise from fetchData call or undefined
   */
  const nextPage = (
    fetchDataFn: () => Promise<any>
  ): Promise<any> | undefined => {
    if (pagination.value.page < pagination.value.lastPage) {
      pagination.value.page += 1;
      return fetchDataFn();
    }
  };

  /**
   * Navigate to previous page if available
   *
   * @param fetchDataFn - Function to call for fetching data
   * @returns Promise from fetchData call or undefined
   */
  const prevPage = (
    fetchDataFn: () => Promise<any>
  ): Promise<any> | undefined => {
    if (pagination.value.page > 1) {
      pagination.value.page -= 1;
      return fetchDataFn();
    }
  };

  /**
   * Update sort field and reset to first page
   *
   * @param sortValue - New sort field
   * @param fetchDataFn - Function to call for fetching data
   * @returns Promise from fetchData call
   */
  const updateSort = (
    sortValue: string,
    fetchDataFn: () => Promise<any>
  ): Promise<any> => {
    pagination.value.sortBy = sortValue;
    pagination.value.page = 1;
    // Reset virtual scroll state
    hasMoreData.value = true;
    // Clear existing items to avoid showing old data while loading
    items.value = [];
    return fetchDataFn();
  };

  /**
   * Update items per page and reset to first page
   *
   * @param perPage - New items per page count
   * @param fetchDataFn - Function to call for fetching data
   * @returns Promise from fetchData call
   */
  const updatePerPage = (
    perPage: number,
    fetchDataFn: () => Promise<any>
  ): Promise<any> => {
    pagination.value.perPage = perPage;
    pagination.value.page = 1;
    return fetchDataFn();
  };

  /**
   * Reset pagination to first page without fetching data
   */
  const resetPagination = (): void => {
    pagination.value.page = 1;
    hasMoreData.value = true;
  };

  /**
   * Load more data for virtual scrolling by fetching next page and appending
   *
   * @param fetchNextPageFn - Function that fetches next page data
   * @returns Promise resolving to new items or empty array
   */
  const loadMoreData = async (
    fetchNextPageFn: () => Promise<T[]>
  ): Promise<T[]> => {
    if (loadingMore.value || !hasMoreData.value) return [];

    loadingMore.value = true;
    try {
      const nextPageData = await fetchNextPageFn();
      if (!nextPageData || nextPageData.length === 0) {
        hasMoreData.value = false;
      }
      return nextPageData;
    } catch (error) {
      console.error('Error loading more data:', error);
      return [];
    } finally {
      loadingMore.value = false;
    }
  };

  /**
   * Generate filter details summary string
   *
   * @param entityName - Singular name of the entity (e.g., 'role', 'permission')
   * @param pluralForm - Optional plural form (defaults to entityName + 's')
   * @returns Formatted HTML string showing loaded/total counts
   */
  const getFilterDetails = (
    entityName: string,
    pluralForm?: string
  ): string => {
    const totalItems = pagination.value.total ?? 0;
    const loadedItems = items.value.length ?? 0;
    const plural = pluralForm ?? `${entityName}s`;

    if (totalItems === 0) {
      return `No ${plural} found`;
    }

    if (loadedItems === totalItems) {
      return `<strong>${totalItems}</strong> ${
        totalItems === 1 ? entityName : plural
      } found`;
    }

    return `Showing <strong>${loadedItems}</strong> of <strong>${totalItems}</strong> ${
      totalItems === 1 ? entityName : plural
    }`;
  };

  /**
   * Handle virtual scroll events
   *
   * @param details - Virtual scroll details from q-table
   * @param loadMoreFn - Function to call when more data needs loading
   */
  const onVirtualScroll = (
    details: { to: number },
    loadMoreFn: () => Promise<any>
  ): void => {
    const { to } = details;
    const totalLoadedItems = items.value.length;

    // Only trigger when user is within 5 rows of the end
    // This prevents loading too many pages prematurely while ensuring smooth UX
    const triggerThreshold = Math.max(
      totalLoadedItems - 5,
      totalLoadedItems * 0.85
    );

    // If scrolling within 5 rows of the end (or 85% through) and have more data to load
    if (
      to >= triggerThreshold &&
      hasMoreData.value &&
      !loadingMore.value &&
      !loading.value &&
      pagination.value.page < pagination.value.lastPage
    ) {
      loadMoreFn();
    }
  };

  /**
   * Creates a configured fetch function for the store
   *
   * @param config - Configuration for the fetcher
   * @returns Fetch function that handles both initial fetch and load more
   */
  const createDataFetcher = (config: FetcherConfig) => {
    return async (options?: { append?: boolean }): Promise<T[]> => {
      const append = options?.append ?? false;

      // Guard against fetching beyond last page when appending
      if (append) {
        if (pagination.value.page >= pagination.value.lastPage) return [];
        pagination.value.page += 1;
        loadingMore.value = true;
      } else {
        loading.value = true;
      }

      try {
        const queryString = config.buildQueryString();
        const response = await api.get(`${config.endpoint}?${queryString}`);
        const newItems = getResponseData(response) as T[];

        items.value = append ? [...items.value, ...newItems] : newItems;

        if (response.data?.meta?.page) {
          updatePagination(response.data.meta.page);
        }

        config.onSuccess?.();
        return newItems;
      } catch (error: any) {
        console.error(`Error fetching data from ${config.endpoint}:`, error);
        if (!append) items.value = [];

        config.onError?.(error);

        if (config.rethrowStatuses?.includes(error?.status)) {
          throw error;
        }

        return [];
      } finally {
        loading.value = false;
        loadingMore.value = false;
      }
    };
  };

  return {
    // Reactive state (make read-only for external access)
    items,
    loading,
    pagination,
    hasMoreData,
    loadingMore,

    // Methods
    buildQueryParams,
    updatePagination,
    nextPage,
    prevPage,
    updateSort,
    updatePerPage,
    resetPagination,
    loadMoreData,
    onVirtualScroll,
    createDataFetcher,
    getFilterDetails,
  };
}

/**
 * Composable for connecting stores to page components
 * Provides a clean interface for pages to interact with paginated stores
 *
 * @param store - Store instance with pagination functionality
 * @returns Object with computed refs to store state and methods
 */
export function useStorePagination(store: any) {
  return {
    // Direct references to store state
    data: store.items,
    pagination: store.pagination,
    loading: store.loading,

    // Direct calls to store methods
    nextPage: store.nextPage,
    prevPage: store.prevPage,
    updateSort: store.updateSort,
    updatePerPage: store.updatePerPage,
    fetchData: store.fetchData,
  };
}
